import { useState, useCallback, useMemo } from 'react';

export type UseTableRowClickReturn = {
  /** ID of the clicked order (null if no order clicked) */
  clickedOrderId: string | null;
  /** Handler to set the clicked order ID */
  handleTableRowClick: (orderId: string) => void;
  /** Reset the clicked order ID to null */
  resetClicked: () => void;
};

/**
 * Hook for managing table row click state for order modals
 *
 * This hook tracks which order row was clicked in the table to determine
 * which order modal should be displayed.
 *
 * @returns Click state and handlers
 *
 * @example
 * ```tsx
 * const { clickedOrderId, handleTableRowClick, resetClicked } = useTableRowClick();
 *
 * // In table row
 * <tr onClick={() => handleTableRowClick(order.id)}>
 *   ...
 * </tr>
 *
 * // In modal
 * <OrderModal
 *   isOpen={clickedOrderId !== null}
 *   orderId={clickedOrderId}
 *   onClose={resetClicked}
 * />
 * ```
 */
export const useTableRowClick = (): UseTableRowClickReturn => {
  const [clickedOrderId, setClickedOrderId] = useState<string | null>(null);

  const handleTableRowClick = useCallback((orderId: string) => {
    setClickedOrderId(orderId);
  }, []);

  const resetClicked = useCallback(() => {
    setClickedOrderId(null);
  }, []);

  return useMemo(
    () => ({
      clickedOrderId,
      handleTableRowClick,
      resetClicked,
    }),
    [clickedOrderId, handleTableRowClick, resetClicked]
  );
};
